import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database } from "@/integrations/supabase/types";
import { CASE_TAG_VALUES, type CaseTag } from "@/lib/cases/tags";
import { extractCaseImagePaths } from "@/lib/cases/parseModules";
import { extractVimeoId, rewriteInlineStorageImages, signMany } from "@/lib/public-content.server";
import { permanentImageUrl } from "@/lib/permanent-image";

export type TestimonialCategory = Database["public"]["Enums"]["testimonial_category"];

export type TestimonialMediaKind = "photo" | "video";

export type PublicTestimonial = {
  id: string;
  author_name: string;
  author_role: string | null;
  quote: string | null;
  categories: TestimonialCategory[];
  priority: Database["public"]["Enums"]["testimonial_priority"];
  photo_url: string | null;
  media_kind: TestimonialMediaKind;
  vimeo_id: string | null;
  video_url: string | null;
  video_poster_url: string | null;
};

export type PublicClientLogo = {
  id: string;
  name: string;
  sort_order: number | null;
  logo_url: string | null;
  categories: TestimonialCategory[];
};

export type PublicBlogListItem = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string;
  tags: string[] | null;
  reading_time: string | null;
  cover_url: string | null;
  /** Ticket KI1: dauerhaft gültige Teilen-Adresse des Beitragsbilds. */
  cover_share_url: string | null;
  published_at: string | null;
  created_at: string;
};

export type PublicBlogPost = PublicBlogListItem & {
  content_md: string;
  author: string | null;
  author_image_url: string | null;
  author_bio: string | null;
};

export const getPublishedTestimonials = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicTestimonial[]> => {
    const { data, error } = await supabaseAdmin
      .from("testimonials")
      .select(
        "id, author_name, author_role, quote, categories, priority, photo_path, media_kind, video_path, video_poster_path, vimeo_url",
      )
      .eq("published", true)
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    const [photos, videos] = await Promise.all([
      signMany("testimonials", [
        ...rows.map((r) => r.photo_path),
        ...rows.map((r) => r.video_poster_path),
      ]),
      signMany(
        "testimonial-videos",
        rows.map((r) => (r.vimeo_url ? null : r.video_path)),
      ),
    ]);
    return rows.map((row) => {
      const vimeo_id = extractVimeoId(row.vimeo_url);
      return {
        id: row.id,
        author_name: row.author_name,
        author_role: row.author_role,
        quote: row.quote,
        categories: row.categories,
        priority: row.priority,
        photo_url: row.photo_path ? photos[row.photo_path] ?? null : null,
        media_kind: (row.media_kind === "video" ? "video" : "photo") as TestimonialMediaKind,
        vimeo_id,
        video_url:
          !vimeo_id && row.video_path ? videos[row.video_path] ?? null : null,
        video_poster_url: row.video_poster_path
          ? photos[row.video_poster_path] ?? null
          : null,
      };
    });
  },
);


export const getPublishedClientLogos = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicClientLogo[]> => {
    const { data, error } = await supabaseAdmin
      .from("client_logos")
      .select("id, name, sort_order, logo_path, categories")
      .eq("published", true)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    const logos = await signMany("client-logos", rows.map((r) => r.logo_path));
    return rows
      .map((row) => ({
        id: row.id,
        name: row.name,
        sort_order: row.sort_order,
        logo_url: row.logo_path ? logos[row.logo_path] ?? null : null,
        categories: (row.categories ?? []) as TestimonialCategory[],
      }))
      .filter((l) => !!l.logo_url);
  },
);


export const getPublishedBlogPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicBlogListItem[]> => {
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select(
        "id, slug, title, excerpt, category, tags, reading_time, cover_image_path, published_at, created_at",
      )
      .eq("published", true)
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    const covers = await signMany(
      "blog-covers",
      rows.map((r) => r.cover_image_path),
    );
    return rows.map((row) => ({
      id: row.id,
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt,
      category: row.category,
      tags: row.tags,
      reading_time: row.reading_time,
      cover_url: row.cover_image_path ? covers[row.cover_image_path] ?? null : null,
      cover_share_url: row.cover_image_path
        ? permanentImageUrl("blog", row.cover_image_path)
        : null,
      published_at: row.published_at,
      created_at: row.created_at,
    }));
  },
);

export const getPublishedBlogPost = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => z.object({ slug: z.string().min(1).max(255) }).parse(data))
  .handler(async ({ data }): Promise<PublicBlogPost | null> => {
    const { data: row, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*, author_ref:author_profile_id(id, full_name, first_name, last_name, bio, photo_path)")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const ref = (row as unknown as {
      author_ref: {
        id: string;
        full_name: string | null;
        first_name: string | null;
        last_name: string | null;
        bio: string | null;
        photo_path: string | null;
      } | null;
    }).author_ref;
    const refName = ref
      ? (ref.full_name?.trim() ||
          [ref.first_name, ref.last_name].filter(Boolean).join(" ").trim() ||
          null)
      : null;
    const resolvedName = refName ?? row.author ?? null;
    // Team-Fotos liegen in staff-photos, legacy Autoren-Fotos in blog-covers/authors/*
    const refPhoto = ref?.photo_path ?? null;
    const legacyPhoto = row.author_image_path ?? null;
    const resolvedBio = ref?.bio?.trim() ? ref.bio : null;
    const [cover, staffAvatar, legacyAvatar, rewrittenContent] = await Promise.all([
      signMany("blog-covers", [row.cover_image_path]),
      signMany("staff-photos", [refPhoto && !refPhoto.startsWith("authors/") ? refPhoto : null]),
      signMany("blog-covers", [
        refPhoto && refPhoto.startsWith("authors/") ? refPhoto : legacyPhoto,
      ]),
      rewriteInlineStorageImages(row.content_md ?? ""),
    ]);
    let author_image_url: string | null = null;
    if (refPhoto) {
      author_image_url = refPhoto.startsWith("authors/")
        ? legacyAvatar[refPhoto] ?? null
        : staffAvatar[refPhoto] ?? null;
    }
    if (!author_image_url && legacyPhoto) {
      author_image_url = legacyAvatar[legacyPhoto] ?? null;
    }
    return {
      id: row.id,
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt,
      category: row.category,
      tags: row.tags,
      reading_time: row.reading_time,
      cover_url: row.cover_image_path ? cover[row.cover_image_path] ?? null : null,
      cover_share_url: row.cover_image_path
        ? permanentImageUrl("blog", row.cover_image_path)
        : null,
      published_at: row.published_at,
      created_at: row.created_at,
      content_md: rewrittenContent,
      author: resolvedName,
      author_image_url,
      author_bio: resolvedBio,
    };
  });

export const getRelatedBlogPosts = createServerFn({ method: "GET" })
  .inputValidator((data: { category: string; excludeSlug: string; limit?: number }) =>
    z
      .object({
        category: z.string().min(1).max(100),
        excludeSlug: z.string().min(1).max(255),
        limit: z.number().int().min(1).max(12).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }): Promise<PublicBlogListItem[]> => {
    const limit = data.limit ?? 3;
    const { data: rows, error } = await supabaseAdmin
      .from("blog_posts")
      .select(
        "id, slug, title, excerpt, category, tags, reading_time, cover_image_path, published_at, created_at",
      )
      .eq("published", true)
      .eq("category", data.category)
      .neq("slug", data.excludeSlug)
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    const list = rows ?? [];
    const covers = await signMany("blog-covers", list.map((r) => r.cover_image_path));
    return list.map((row) => ({
      id: row.id,
      slug: row.slug,
      title: row.title,
      excerpt: row.excerpt,
      category: row.category,
      tags: row.tags,
      reading_time: row.reading_time,
      cover_url: row.cover_image_path ? covers[row.cover_image_path] ?? null : null,
      cover_share_url: row.cover_image_path
        ? permanentImageUrl("blog", row.cover_image_path)
        : null,
      published_at: row.published_at,
      created_at: row.created_at,
    }));
  });

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

export type PublicCase = {
  id: string;
  slug: string;
  title: string;
  client_name: string | null;
  excerpt: string | null;
  reading_time: string | null;
  tags: string[] | null;
  postal_code: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  cover_url: string | null;
  content_md: string;
  media_urls: Record<string, string>;
  // Ticket AV6: Autorenangaben bei Cases entfallen (nie öffentlich dargestellt).
  testimonial_quote: string | null;
  testimonial_author: string | null;
  testimonial_role: string | null;
  testimonial_image_url: string | null;
  published_at: string | null;
  created_at: string;
};

export type PublicCaseListItem = {
  id: string;
  slug: string;
  title: string;
  client_name: string | null;
  excerpt: string | null;
  reading_time: string | null;
  tags: string[] | null;
  postal_code: string | null;
  city: string | null;
  cover_url: string | null;
  priority: Database["public"]["Enums"]["case_priority"];
  published_at: string | null;
  created_at: string;
};

export const getPublishedCase = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) =>
    z.object({ slug: z.string().min(1).max(255) }).parse(data),
  )
  .handler(async ({ data }): Promise<PublicCase | null> => {
    const { data: row, error } = await supabaseAdmin
      .from("cases")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const tRow = row as typeof row & {
      testimonial_quote: string | null;
      testimonial_author: string | null;
      testimonial_role: string | null;
      testimonial_image_path: string | null;
    };
    const inlinePaths = extractCaseImagePaths(row.content_md ?? "");
    const [covers, testimonialImgs, inlineImgs, rewrittenContent] =
      await Promise.all([
        signMany("case-covers", [row.cover_image_path]),
        signMany("case-covers", [tRow.testimonial_image_path]),
        signMany("case-covers", inlinePaths),
        rewriteInlineStorageImages(row.content_md ?? ""),
      ]);
    return {
      id: row.id,
      slug: row.slug,
      title: row.title,
      client_name: row.client_name ?? null,
      excerpt: row.excerpt,
      reading_time: row.reading_time,
      tags: row.tags,
      postal_code: row.postal_code,
      city: row.city,
      latitude: row.latitude,
      longitude: row.longitude,
      cover_url: row.cover_image_path ? covers[row.cover_image_path] ?? null : null,
      content_md: rewrittenContent,
      media_urls: inlineImgs,
      testimonial_quote: tRow.testimonial_quote,
      testimonial_author: tRow.testimonial_author,
      testimonial_role: tRow.testimonial_role,
      testimonial_image_url: tRow.testimonial_image_path
        ? testimonialImgs[tRow.testimonial_image_path] ?? null
        : null,
      published_at: row.published_at,
      created_at: row.created_at,
    };
  });

export const getCasesByTag = createServerFn({ method: "GET" })
  .inputValidator((data: { tag: CaseTag }) =>
    z.object({ tag: z.enum(CASE_TAG_VALUES as unknown as [CaseTag, ...CaseTag[]]) }).parse(data),
  )
  .handler(async ({ data }): Promise<PublicCaseListItem[]> => {
    const { data: rows, error } = await supabaseAdmin
      .from("cases")
      .select(
        "id, slug, title, client_name, excerpt, reading_time, tags, postal_code, city, cover_image_path, priority, published_at, created_at",
      )
      .eq("published", true)
      .contains("tags", [data.tag])
      .order("priority", { ascending: true })
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const list = rows ?? [];
    const covers = await signMany("case-covers", list.map((r) => r.cover_image_path));
    return list.map((row) => ({
      id: row.id,
      slug: row.slug,
      title: row.title,
      client_name: row.client_name ?? null,
      excerpt: row.excerpt,
      reading_time: row.reading_time,
      tags: row.tags,
      postal_code: row.postal_code,
      city: row.city,
      cover_url: row.cover_image_path ? covers[row.cover_image_path] ?? null : null,
      priority: row.priority,
      published_at: row.published_at,
      created_at: row.created_at,
    }));
  });

export const getAllPublicCases = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicCaseListItem[]> => {
    const { data: rows, error } = await supabaseAdmin
      .from("cases")
      .select(
        "id, slug, title, client_name, excerpt, reading_time, tags, postal_code, city, cover_image_path, priority, published_at, created_at",
      )
      .eq("published", true)
      .order("priority", { ascending: true })
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const list = rows ?? [];
    const covers = await signMany("case-covers", list.map((r) => r.cover_image_path));
    return list.map((row) => ({
      id: row.id,
      slug: row.slug,
      title: row.title,
      client_name: row.client_name ?? null,
      excerpt: row.excerpt,
      reading_time: row.reading_time,
      tags: row.tags,
      postal_code: row.postal_code,
      city: row.city,
      cover_url: row.cover_image_path ? covers[row.cover_image_path] ?? null : null,
      priority: row.priority,
      published_at: row.published_at,
      created_at: row.created_at,
    }));
  },
);

export type PublishedCaseListEntry = {
  slug: string;
  published_at: string | null;
  updated_at: string;
};

export const listPublishedCases = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublishedCaseListEntry[]> => {
    const { data, error } = await supabaseAdmin
      .from("cases")
      .select("slug, published_at, updated_at")
      .eq("published", true);
    if (error) throw new Error(error.message);
    return data ?? [];
  },
);
